// --- React & Redux
import React from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
// --- Actions & Hooks
import { useUsersData } from "./hooks/useUsersData";
import { usePermissions } from "../../utils/permissions";
import { useStoredItemsPerPageSync } from "tap/utils/hooks/useStoredItemsPerPageSync.js";
import { PAGINATION_STORAGE_KEYS } from "tap/utils/paginationPreferences.js";
// --- External UI Libraries
import { Col, Row } from "antd";
import Typography from "@dtci/spellbook/Typography";
import Button from "@dtci/spellbook/Button";
// --- Components
import { PaginationBar } from "../../components/PaginationBar";
import { UsersSearchForm } from "./components/search-form";
import { UsersGrid } from "./components/grid";
import { TwoRowsLayout } from "./layout/two-row";
import ToastNotificationProvider from "../../components/ToastNotifications";
// --- Utilities
import { getItemRange } from "../../utils/pagination";
import { parseSearchParams, updateSearchParams } from "../../utils/urlParams";
import { exportToCsv } from "../../utils/exportToCsv";
// --- Config
import { mapRolesToLabels } from "../../config/userRoles";
import { EXPORT_ALL_RANGE } from "../../store/users/constants";
// --- Styles
import "./styles.scss";

/**
 * UsersPage displays and manages the list of users.
 *
 * Features:
 * - Displays a paginated, searchable, and sortable user grid.
 * - Allows creating new users via a drawer form.
 * - Supports exporting user data to CSV.
 * - Provides refresh and pagination controls.
 * - Uses Redux for state management and Ant Design/Spellbook for UI.
 *
 * @component
 * @returns {JSX.Element} The rendered users management page.
 */
export const UsersPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fetchUsers, updateUsers, itemsPerPage, userRange } = useUsersData();
  const { totalPages, currentPage, totalItems, pageSizeOptions } = useSelector(
    (s) => s.users,
  );
  const { isAdmin } = usePermissions();

  useStoredItemsPerPageSync({
    storageKey: PAGINATION_STORAGE_KEYS.users,
    itemsPerPage,
    rangeKey: "userRange",
    onSync: updateUsers,
  });

  const handleChange = async (pageNumber) => {
    if (pageNumber === currentPage) return;

    const nextPageRange = getItemRange(pageNumber, itemsPerPage);
    const data = await fetchUsers(nextPageRange);

    if (data) {
      updateUsers({
        users: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber,
        userRange: nextPageRange,
      });
    }
  };

  const handleRefresh = () => {
    updateUsers({ refreshUsers: true });
  };

  const handleExport = async () => {
    const data = await fetchUsers(EXPORT_ALL_RANGE);

    if (data) {
      const usersExport = (data?.content || []).map((user) => ({
        id: user.id,
        email: user.username,
        fullName: user.fullName,
        rightslineId: user.rightslineId || "",
        roles: mapRolesToLabels(user.spaUserRoleIds),
        createdAt: user.createdAt,
        modifiedAt: user.modifiedAt,
      }));
      exportToCsv(usersExport, "users");
    }
  };

  const handlePageSizeChange = (newSize) => {
    const currentParams = parseSearchParams(location.search);
    updateSearchParams(location, navigate, {
      ...currentParams,
      pageSize: newSize,
    });
  };

  const handleCreateUser = () => {
    updateUsers({
      drawerMode: "create",
      selectedUser: null,
    });
  };

  return (
    <>
      <Row className="users-page-header">
        <Col>
          <Typography variant="T50" style={{ marginLeft: 20 }}>
            Users
          </Typography>
        </Col>
        <Col className="users-page-header-actions">
          {isAdmin && (
            <Button
              iconType="fa-solid fa-plus"
              labelText="Create"
              styleType="link"
              onClick={handleCreateUser}
            >
              Create
            </Button>
          )}
          <Button
            iconType="fa-solid fa-arrow-down-to-line"
            labelText="Export"
            styleType="link"
            onClick={handleExport}
            disabled={totalItems === 0}
          >
            Export
          </Button>
          <Button
            iconType="fa-solid fa-rotate"
            labelText="Refresh"
            styleType="link"
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Col>
      </Row>

      <TwoRowsLayout>
        <main data-position="top">
          <UsersSearchForm />
        </main>
        <main data-position="bottom">
          <UsersGrid />
        </main>
      </TwoRowsLayout>

      <PaginationBar
        totalPages={totalPages}
        currentPage={currentPage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handleChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={pageSizeOptions}
      />
      <ToastNotificationProvider />
    </>
  );
};
